<template>
    <div class="structure-slot-product-description-reviews">
        <div class="lg:hidden">
            <div v-if="ingredientsActive" class="flex">
                <ProductDetailTabContentIngredients
                    :custom-fields="product?.customFields"
                />
            </div>

            <MiscModal v-if="nutritionActive" button-classes="w-full">
                <template #button>
                    <div class="flex justify-between w-full">
                        <div class="text-xl text-link font-semibold pb-4 text-left">
                            {{ t('structure.productTabs.nutrition') }}
                        </div>
                        <MiscIcon
                            :name="'chevron-right'"
                            :size="'lg'"
                        />
                    </div>
                </template>

                <template #content>
                    <ProductDetailTabContentNutrition
                        :custom-fields="product?.customFields"
                        :manufacturer="product?.manufacturer"
                    />
                </template>
            </MiscModal>

            <MiscModal v-if="product?.customFields?.additional_information && product.customFields.additional_information.trim()" button-classes="w-full">
                <template #button>
                    <div class="flex justify-between w-full">
                        <div class="text-xl text-link font-semibold pb-4 text-left">
                            {{ t('product.tabsAdditionalInformations.title') }}
                        </div>
                        <MiscIcon
                            :name="'chevron-right'"
                            :size="'lg'"
                        />
                    </div>
                </template>

                <template #content>
                    <ProductDetailTabAdditionalInformations
                        :custom-fields="product?.customFields"
                    />
                </template>
            </MiscModal>
        </div>

        <div class="hidden lg:block">
            <div class="tabs flex h-14" role="tablist">
                <a
                    v-if="ingredientsActive"
                    :class="{ 'tab-active bg-base-200': activeTab === 'tab-1'}"
                    class="tab tab-lg tab-bordered rounded-lg rounded-b-none text-xl font-semibold leading-7 px-6 pt-3 pb-3 h-auto text-base-content"
                    role="tab"
                    @click="activeTab = 'tab-1'"
                >
                    {{ t('structure.productTabs.ingredients') }}
                </a>
                <a
                    v-if="nutritionActive"
                    :class="{ 'tab-active bg-base-200': activeTab === 'tab-2'}"
                    class="tab tab-lg tab-bordered rounded-lg rounded-b-none text-xl font-semibold leading-7 px-6 pt-3 pb-3 h-auto text-base-content"
                    role="tab"
                    @click="activeTab = 'tab-2'"
                >
                    {{ t('structure.productTabs.nutrition') }}
                </a>
                <a
                    v-if="product?.customFields?.additional_information && product.customFields.additional_information.trim()"
                    :class="{ 'tab-active bg-base-200': activeTab === 'tab-3'}"
                    class="tab tab-lg tab-bordered rounded-lg rounded-b-none text-xl font-semibold leading-7 px-6 pt-3 pb-3 h-auto text-base-content"
                    role="tab"
                    @click="activeTab = 'tab-3'"
                >
                    {{ t('product.tabsAdditionalInformations.title') }}
                </a>
            </div>

            <div
                class="bg-base-200 rounded-lg"
                :class="{
                    'rounded-tl-none': activeTab === firstTab
                }"
            >
                <transition name="fade" mode="out-in">
                    <div v-if="activeTab === 'tab-1' && ingredientsActive" class="px-6 py-8 text-lg">
                        <ProductDetailTabContentIngredients
                            :custom-fields="product?.customFields"
                        />
                    </div>
                    <div v-else-if="activeTab === 'tab-2' && nutritionActive" class="px-6 py-8">
                        <ProductDetailTabContentNutrition
                            :custom-fields="product?.customFields"
                            :manufacturer="product?.manufacturer"
                        />
                    </div>
                    <div v-else-if="activeTab === 'tab-3' && product?.customFields?.additional_information && product.customFields.additional_information.trim()" class="px-6 py-8">
                        <ProductDetailTabAdditionalInformations
                            :custom-fields="product?.customFields"
                        />
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, inject, ref} from 'vue'
import type { HblProduct, HblSlot } from '@/utils/types'
import { hblDetailData } from '@/utils/helper'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
    content: HblSlot
}>()

const product = inject<HblProduct>(hblDetailData)

const ingredientsActive = computed(() => {
    const fields = product?.customFields;
    return fields?.ingredients || fields?.ingredients_traces_of;
});

const nutritionActive = computed(() => {
    const fields = product?.customFields;
    return product?.manufacturer?.description || fields?.ingredients_traces_of ||
        fields?.cool_storage || fields?.caloric_value_kj > 0 ||
        fields?.additional_nutrition_values || fields?.nutritional_recommendation;
});

const firstTab = computed(() => {
    if (ingredientsActive.value) return 'tab-1';
    if (nutritionActive.value) return 'tab-2';
    return 'tab-3';
});

const activeTab = ref(firstTab.value);
</script>
